// src/app/components/reporting/components/library-phase.tsx
//
// Phase 1 of Insights & Reports: the saved-insight library.
//
// Source: handoff-reporting §4 and the "Library" screen of
// `Insights and Reports - Polished.dc.html`.

import { useId } from "react";
import { Bookmark, Check, Sparkles } from "lucide-react";
import { Button, EmptyState, Icon, Spinner } from "@/app/components/kit";
import { moduleIdentity } from "../module-identity";
import type { ModuleIdentity } from "../module-identity";
import type { SavedInsight } from "../types";

const ALL = "all";

/* ── the filter row ─────────────────────────────────────────────────────────
   `All` plus one chip per module PRESENT in the data. A chip for a module with
   nothing in it would be a filter that can only ever produce an empty list. */

interface Bucket {
  key: string;
  label: string;
  count: number;
  identity: ModuleIdentity | null;
}

function buckets(insights: readonly SavedInsight[]): Bucket[] {
  const byKey = new Map<string, Bucket>();
  for (const i of insights) {
    const identity = moduleIdentity(i.module);
    const found = byKey.get(identity.key);
    if (found) found.count += 1;
    else byKey.set(identity.key, { key: identity.key, label: identity.label, count: 1, identity });
  }
  return [
    { key: ALL, label: "All", count: insights.length, identity: null },
    ...[...byKey.values()].sort((a, b) => a.label.localeCompare(b.label)),
  ];
}

/* ── one card ─────────────────────────────────────────────────────────────── */

function InsightRow({
  insight,
  selected,
  onToggle,
  onAsk,
}: {
  insight: SavedInsight;
  selected: boolean;
  onToggle: (id: number) => void;
  onAsk?: (insight: SavedInsight) => void;
}) {
  const identity = moduleIdentity(insight.module);
  const savedFrom = insight.saved_from?.trim();
  const titleId = useId();

  return (
    /* The WHOLE card toggles selection, so the card is the control.
     *
     * It is a div with role="checkbox" rather than a <button>, and both halves
     * of that matter. A <button> cannot legally contain the paragraph this card
     * draws, and it cannot contain the "Ask" control either — interactive
     * content nested inside interactive content, which is exactly the shape a
     * screen reader cannot present. role="checkbox" says what the card IS
     * (many can be chosen), carries its own state in aria-checked, and leaves
     * Ask free to be a real button. */
    <div
      className="upm-ir-card"
      role="checkbox"
      aria-checked={selected}
      aria-labelledby={titleId}
      tabIndex={0}
      onClick={() => onToggle(insight.id)}
      onKeyDown={(e) => {
        if (e.key !== " " && e.key !== "Enter") return;
        e.preventDefault();
        onToggle(insight.id);
      }}
    >
      <span className="upm-ir-card__box" aria-hidden="true">
        {selected ? <Icon icon={Check} size="sm" /> : null}
      </span>

      <div className="upm-ir-card__main">
        <div className="upm-ir-card__head">
          {/* The metric renders only when there is one. An insight with no
              figure gets no figure — never a zero, never a dash. */}
          {insight.metric ? (
            <span className="upm-ir-card__metric" style={{ color: identity.color }}>
              {insight.metric}
            </span>
          ) : null}
          <span className="upm-ir-card__title" id={titleId}>
            {insight.title}
          </span>
        </div>

        {insight.subtitle ? <p className="upm-ir-card__sub">{insight.subtitle}</p> : null}

        <div className="upm-ir-card__meta">
          <span
            className="upm-ir-modchip"
            style={{
              background: identity.tint,
              borderColor: identity.border,
              color: identity.color,
            }}
          >
            <span className="upm-ir-modchip__dot" />
            {identity.label}
          </span>
          {savedFrom ? <span>{savedFrom}</span> : null}
          {savedFrom ? <span className="upm-ir-card__sep">·</span> : null}
          <span>{relative(insight.created_at)}</span>
        </div>
      </div>

      {onAsk ? (
        // ⚠ stopPropagation is load-bearing. The parent card toggles selection,
        // so without it every click on Ask also adds or wipes a selection —
        // this module's one real click-target conflict. `reporting:check`
        // asserts it by clicking Ask and counting the selection either side.
        <button
          type="button"
          className="upm-ir-card__ask"
          title="Why this matters"
          onClick={(e) => {
            e.stopPropagation();
            onAsk(insight);
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Icon icon={Sparkles} size="sm" />
          Ask
        </button>
      ) : null}
    </div>
  );
}

/**
 * "2h ago" from a timestamp.
 *
 * Rendered from the stored `created_at` rather than passed in, because a card
 * that was handed both a date and a phrase can show a phrase the date does not
 * support. An unparseable date renders nothing rather than "Invalid Date" or a
 * zero — the reading is absent, so the element is.
 */
function relative(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/* ── the phase ────────────────────────────────────────────────────────────── */

export interface LibraryPhaseProps {
  insights: SavedInsight[];
  isLoading: boolean;
  error: Error | null;
  filter: string;
  onFilterChange: (key: string) => void;
  selectedIds: number[];
  onToggle: (id: number) => void;
  onClear: () => void;
  onCompose: () => void;
  onAsk?: (insight: SavedInsight) => void;
}

export function LibraryPhase({
  insights,
  isLoading,
  error,
  filter,
  onFilterChange,
  selectedIds,
  onToggle,
  onClear,
  onCompose,
  onAsk,
}: LibraryPhaseProps) {
  const rows = buckets(insights);
  const shown =
    filter === ALL ? insights : insights.filter((i) => moduleIdentity(i.module).key === filter);
  const count = selectedIds.length;

  if (isLoading) {
    return (
      <div className="upm-ir-cards">
        <Spinner label="Loading saved insights" />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon={Bookmark}
        title="Saved insights could not be loaded"
        description={error.message}
      />
    );
  }

  if (insights.length === 0) {
    return (
      <EmptyState
        icon={Bookmark}
        title="Nothing saved yet"
        description="Save a reading from anywhere in the platform and it collects here, ready to compose into a report."
      />
    );
  }

  return (
    <>
      <div className="upm-ir-filters" role="group" aria-label="Filter by module">
        {rows.map((b) => (
          <button
            key={b.key}
            type="button"
            className="upm-ir-filter"
            aria-pressed={filter === b.key}
            onClick={() => onFilterChange(b.key)}
          >
            {b.identity ? (
              <span
                className="upm-ir-filter__dot"
                style={{ background: b.identity.color }}
                aria-hidden="true"
              />
            ) : null}
            {b.label}
            <span className="upm-ir-filter__count">{b.count}</span>
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <p className="upm-ir-card__sub">No insights match this filter.</p>
      ) : (
        <div className="upm-ir-cards">
          {shown.map((insight) => (
            <InsightRow
              key={insight.id}
              insight={insight}
              selected={selectedIds.includes(insight.id)}
              onToggle={onToggle}
              onAsk={onAsk}
            />
          ))}
        </div>
      )}

      {/* The action on the selection lives WITH the selection. In the filter
          row it wrapped mid-row and stranded the primary action. */}
      {count > 0 ? (
        <div className="upm-ir-selbar" role="region" aria-label="Selected insights">
          <span className="upm-ir-selbar__count">{count}</span>
          <span className="upm-ir-selbar__label">
            {count === 1 ? "insight selected" : "insights selected"}
          </span>
          <Button variant="ghost" onClick={onClear}>
            Clear
          </Button>
          <span className="upm-ir-selbar__cta">
            {/* The DC draws a TRAILING arrow here. The kit's Button takes a
                leading icon only, and says why in its own redline, so the
                arrow is dropped rather than moved: the label already says
                which direction this goes. */}
            <Button onClick={onCompose}>Compose from selection</Button>
          </span>
        </div>
      ) : null}
    </>
  );
}
